import { atom, useRecoilState, useResetRecoilState, useSetRecoilState } from "recoil";
import { useCallback } from "react";
import { randomString } from "../../Functions";
import { LikesState, UseLikesReturn } from "./type";
import { postsState } from "./Posts";
import { socialDatabase } from "../../Data/Social/Database";
import sessionFuncs from "../../Data/Session";
import * as socialAPI from "../../Data/Social";

// Likes state atom - only stores liked post IDs as Set
export const likesState = atom<LikesState>({
    key: "likesState" + randomString(4),
    default: {
        likedPostIds: new Set<string>(),
        loading: false,
        error: null
    },
    effects: [
        ({ setSelf }) => {
            const loadUserLikes = async () => {
                try {
                    const currentUser = sessionFuncs.getCurrentSession();
                    if (!currentUser.addressid) return;

                    // Load user's likes from database
                    const userLikes = await socialDatabase.getUserLikes(currentUser.addressid);
                    const likedPostIds = new Set(userLikes.map(like => like.postid));
                    
                    setSelf({
                        likedPostIds,
                        loading: false,
                        error: null
                    });
                } catch (error) {
                    console.error('Failed to load user likes from database:', error);
                }
            };
            
            loadUserLikes();
        }
    ]
});

export const useLikes = (): UseLikesReturn => {
    const [state, setState] = useRecoilState(likesState);
    const setPostsState = useSetRecoilState(postsState);
    const resetLikes = useResetRecoilState(likesState);

    // Check if a post is liked by the current user
    const isPostLiked = useCallback((postid: string): boolean => {
        return state.likedPostIds.has(postid);
    }, [state.likedPostIds]);

    // Like a post
    const likePost = useCallback(async (postid: string): Promise<boolean> => {
        const currentUser = sessionFuncs.getCurrentSession();
        if (!currentUser.addressid) return false;

        // Optimistic updates
        setState(prev => ({
            ...prev,
            likedPostIds: new Set([...prev.likedPostIds, postid])
        }));
        
        setPostsState(prev => ({
            ...prev,
            items: {
                ...prev.items,
                [postid]: prev.items[postid] ? {
                    ...prev.items[postid],
                    like_count: prev.items[postid].like_count + 1,
                    user_liked: true
                } : prev.items[postid]
            }
        }));

        try {
            const result = await socialAPI.likePost({ postid });
            
            if (result) {
                // Save like to local database
                await socialDatabase.saveLikes([{
                    addressid: currentUser.addressid,
                    postid,
                    created_at: Math.floor(Date.now() / 1000)
                }]);
                return true;
            } else {
                // Revert optimistic updates on failure
                setState(prev => {
                    const newSet = new Set(prev.likedPostIds);
                    newSet.delete(postid);
                    return { ...prev, likedPostIds: newSet };
                });
                
                setPostsState(prev => ({
                    ...prev,
                    items: {
                        ...prev.items,
                        [postid]: prev.items[postid] ? {
                            ...prev.items[postid],
                            like_count: Math.max(0, prev.items[postid].like_count - 1),
                            user_liked: false
                        } : prev.items[postid]
                    }
                }));
                return false;
            }
        } catch (error) {
            // Revert optimistic updates on error
            setState(prev => {
                const newSet = new Set(prev.likedPostIds);
                newSet.delete(postid);
                return {
                    ...prev,
                    likedPostIds: newSet,
                    error: error instanceof Error ? error.message : "Failed to like post"
                };
            });
            
            setPostsState(prev => ({
                ...prev,
                items: {
                    ...prev.items,
                    [postid]: prev.items[postid] ? {
                        ...prev.items[postid],
                        like_count: Math.max(0, prev.items[postid].like_count - 1),
                        user_liked: false
                    } : prev.items[postid]
                }
            }));
            
            console.error('Failed to like post:', error);
            return false;
        }
    }, [setState, setPostsState]);

    // Unlike a post
    const unlikePost = useCallback(async (postid: string): Promise<boolean> => {
        const currentUser = sessionFuncs.getCurrentSession();
        if (!currentUser.addressid) return false;

        // Optimistic updates
        setState(prev => {
            const newSet = new Set(prev.likedPostIds);
            newSet.delete(postid);
            return { ...prev, likedPostIds: newSet };
        });
        
        setPostsState(prev => ({
            ...prev,
            items: {
                ...prev.items,
                [postid]: prev.items[postid] ? {
                    ...prev.items[postid],
                    like_count: Math.max(0, prev.items[postid].like_count - 1),
                    user_liked: false
                } : prev.items[postid]
            }
        }));

        try {
            const result = await socialAPI.unlikePost({ postid });
            
            if (result) {
                // Remove like from local database
                await socialDatabase.deleteLike(currentUser.addressid, postid);
                return true;
            } else {
                // Revert optimistic updates on failure
                setState(prev => ({
                    ...prev,
                    likedPostIds: new Set([...prev.likedPostIds, postid])
                }));
                
                setPostsState(prev => ({
                    ...prev,
                    items: {
                        ...prev.items,
                        [postid]: prev.items[postid] ? {
                            ...prev.items[postid],
                            like_count: prev.items[postid].like_count + 1,
                            user_liked: true
                        } : prev.items[postid]
                    }
                }));
                return false;
            }
        } catch (error) {
            // Revert optimistic updates on error
            setState(prev => ({
                ...prev,
                likedPostIds: new Set([...prev.likedPostIds, postid]),
                error: error instanceof Error ? error.message : "Failed to unlike post"
            }));
            
            setPostsState(prev => ({
                ...prev,
                items: {
                    ...prev.items,
                    [postid]: prev.items[postid] ? {
                        ...prev.items[postid],
                        like_count: prev.items[postid].like_count + 1,
                        user_liked: true
                    } : prev.items[postid]
                }
            }));
            
            console.error('Failed to unlike post:', error);
            return false;
        }
    }, [setState, setPostsState]);

    // Sync user likes from API
    const syncUserLikes = useCallback(async (): Promise<void> => {
        const currentUser = sessionFuncs.getCurrentSession();
        if (!currentUser.addressid) return;

        setState(prev => ({ ...prev, loading: true, error: null }));

        try {
            // Fetch user's likes from API
            const apiLikes = await socialAPI.listUserLikes();
            
            if (apiLikes) {
                // Save to local database
                await socialDatabase.saveLikes(apiLikes);
                
                // Update state
                const likedPostIds = new Set(apiLikes.map(like => like.postid));
                setState(prev => ({
                    ...prev,
                    likedPostIds,
                    loading: false
                }));
            } else {
                setState(prev => ({ ...prev, loading: false }));
            }
        } catch (error) {
            setState(prev => ({
                ...prev,
                error: error instanceof Error ? error.message : "Failed to sync user likes",
                loading: false
            }));
            console.error('Failed to sync user likes:', error);
        }
    }, [setState]);

    return {
        likedPostIds: state.likedPostIds,
        loading: state.loading,
        error: state.error,
        
        // Like functions
        isPostLiked,
        likePost,
        unlikePost,
        syncUserLikes,

        reset: resetLikes
    };
};